import type {
  GtfsFieldDefinition,
  GtfsTableDefinition,
} from './define-table.ts';

export type CompiledGtfsColumn = GtfsFieldDefinition & {
  name: string;
  storageKind: 'text' | 'integer' | 'real' | 'json' | 'date' | 'time';
  primaryKey: boolean;
  sqlMinimum?: number;
  sqlMaximum?: number;
};

export type CompiledGtfsTable = GtfsTableDefinition & {
  name: string;
  columns: CompiledGtfsColumn[];
};

export interface GtfsIndexPlan {
  singleColumnIndexes: string[];
  compositeIndexes: string[][];
}

function storageKind(
  field: GtfsFieldDefinition,
): CompiledGtfsColumn['storageKind'] {
  if (field.kind === 'id') return 'text';
  if (field.kind === 'enumeration') {
    return typeof field.values[0] === 'number' ? 'integer' : 'text';
  }
  return field.kind;
}

export function getTableName(definition: GtfsTableDefinition): string {
  if (definition.file === null) return definition.table;
  const extensionIndex = definition.file.lastIndexOf('.');
  return extensionIndex === -1
    ? definition.file
    : definition.file.slice(0, extensionIndex);
}

export function compileTable(
  definition: GtfsTableDefinition,
): CompiledGtfsTable {
  const primaryFields = new Set<string>(definition.primaryKey ?? []);
  const columns = Object.entries(definition.fields).map(([name, field]) => {
    return {
      ...field,
      name,
      storageKind: storageKind(field),
      primaryKey: primaryFields.has(name),
      sqlMinimum: field.minimum,
      sqlMaximum: field.maximum,
    } satisfies CompiledGtfsColumn;
  });

  const compiled: CompiledGtfsTable = {
    ...definition,
    name: getTableName(definition),
    columns,
  };
  return compiled;
}

export function getTimestampColumnName(columnName: string): string {
  return columnName.endsWith('time')
    ? `${columnName}stamp`
    : `${columnName}_timestamp`;
}

/** Returns the columns making up a table's declared key, in manifest order. */
export function getPrimaryKeyColumns(
  table: CompiledGtfsTable,
): CompiledGtfsColumn[] {
  return table.columns.filter((column) => column.primaryKey);
}

/** Builds the index name every database writer uses for the same columns. */
export function getGtfsIndexName(
  tableName: string,
  columns: readonly string[],
): string {
  return `idx_${tableName}_${columns.join('_')}`;
}

export function getGtfsIndexPlan(
  table: CompiledGtfsTable,
  options: { includeGeneratedTimeIndexes: boolean },
): GtfsIndexPlan {
  const singleColumnIndexes = new Set<string>();
  const compositeIndexes: string[][] = [];

  // Generated timestamp columns only exist when node-GTFS manages the schema,
  // so indexes on them are skipped for caller-owned tables.
  const generatedTimeColumns = new Set(
    table.columns
      .filter((column) => column.storageKind === 'time')
      .map((column) => getTimestampColumnName(column.name)),
  );

  const seenCompositeIndexes = new Set<string>();

  for (const configuredIndex of table.storage?.indexes ?? []) {
    let columns =
      typeof configuredIndex === 'string'
        ? [configuredIndex]
        : [...configuredIndex];

    // A composite ending in a generated column keeps its leading columns, which
    // still serve the queries that seek on them.
    if (!options.includeGeneratedTimeIndexes) {
      const generatedIndex = columns.findIndex((column) =>
        generatedTimeColumns.has(column),
      );
      if (generatedIndex !== -1) {
        columns = columns.slice(0, generatedIndex);
      }
    }

    if (columns.length === 0) {
      continue;
    }

    if (columns.length === 1) {
      singleColumnIndexes.add(columns[0]);
      continue;
    }

    const key = columns.join(' ');
    if (!seenCompositeIndexes.has(key)) {
      seenCompositeIndexes.add(key);
      compositeIndexes.push(columns);
    }
  }

  return {
    singleColumnIndexes: [...singleColumnIndexes],
    compositeIndexes,
  };
}
